import { select } from "d3-selection"
import { zoomIdentity } from "d3-zoom"

const MINIMAP_WIDTH = 150
const MINIMAP_HEIGHT = 100
const MINIMAP_PADDING = 8

export function renderMiniMap(container, root, mainSvg, zoomBehavior) {
  removeMiniMap(container)

  const wrapper = document.createElement("div")
  wrapper.className = "tree-minimap"
  container.appendChild(wrapper)

  const svg = select(wrapper).append("svg")
    .attr("width", MINIMAP_WIDTH)
    .attr("height", MINIMAP_HEIGHT)
    .attr("class", "tree-minimap__svg")

  const bounds = computeBounds(root)
  const scale = fitScale(bounds)
  const offsetX = MINIMAP_WIDTH / 2 - (bounds.cx * scale)
  const offsetY = MINIMAP_HEIGHT / 2 - (bounds.cy * scale)

  const g = svg.append("g")
    .attr("transform", `translate(${offsetX},${offsetY}) scale(${scale})`)

  // Draw simplified nodes as dots
  const nodes = root.descendants()
  g.selectAll("circle")
    .data(nodes)
    .join("circle")
    .attr("cx", d => d.x)
    .attr("cy", d => d.y)
    .attr("r", 3 / scale)
    .attr("fill", "#a8a29e")

  // Draw simplified links
  g.selectAll("line")
    .data(root.links())
    .join("line")
    .attr("x1", d => d.source.x).attr("y1", d => d.source.y)
    .attr("x2", d => d.target.x).attr("y2", d => d.target.y)
    .attr("stroke", "#d6d3d1")
    .attr("stroke-width", 1 / scale)

  // Viewport rectangle
  const viewport = svg.append("rect")
    .attr("class", "tree-minimap__viewport")

  syncViewport(viewport, mainSvg, bounds, scale, offsetX, offsetY)

  // Click on minimap to navigate
  svg.on("click", (event) => {
    const [mx, my] = [event.offsetX, event.offsetY]
    const worldX = (mx - offsetX) / scale
    const worldY = (my - offsetY) / scale

    const svgEl = mainSvg.node()
    const cx = svgEl.clientWidth / 2
    const cy = svgEl.clientHeight / 2

    mainSvg.transition().duration(300)
      .call(zoomBehavior.transform, zoomIdentity.translate(cx - worldX, cy - worldY))
  })

  return { viewport, bounds, scale, offsetX, offsetY }
}

export function syncViewport(viewport, mainSvg, bounds, scale, offsetX, offsetY) {
  const svgEl = mainSvg.node()
  if (!svgEl) return

  const transform = mainSvg.property("__zoom") || zoomIdentity
  const w = svgEl.clientWidth / transform.k
  const h = svgEl.clientHeight / transform.k
  const vx = -transform.x / transform.k
  const vy = -transform.y / transform.k

  viewport
    .attr("x", vx * scale + offsetX)
    .attr("y", vy * scale + offsetY)
    .attr("width", w * scale)
    .attr("height", h * scale)
}

export function removeMiniMap(container) {
  const existing = container.querySelector(".tree-minimap")
  if (existing) existing.remove()
}

function computeBounds(root) {
  let minX = Infinity, maxX = -Infinity
  let minY = Infinity, maxY = -Infinity

  root.each(node => {
    minX = Math.min(minX, node.x)
    maxX = Math.max(maxX, node.x)
    minY = Math.min(minY, node.y)
    maxY = Math.max(maxY, node.y)
  })

  return {
    minX, maxX, minY, maxY,
    width: maxX - minX || 1,
    height: maxY - minY || 1,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2
  }
}

function fitScale(bounds) {
  const usable = MINIMAP_WIDTH - MINIMAP_PADDING * 2
  const usableH = MINIMAP_HEIGHT - MINIMAP_PADDING * 2
  return Math.min(usable / bounds.width, usableH / bounds.height, 1)
}
